// @ts-ignore
import {KeycloakInstance, KeycloakProfile} from 'keycloak-js';
import React, {createContext, ReactNode, useEffect, useState} from 'react';
import keycloak from "./KeycloakConfig";
import {TipoUsuarioKeycloak} from "./TipoUsuarioKeycloak";
import {Ajax} from "../utils/Ajax";
import rest from "../utils/EnvConfig";
import {UsuarioKeycloakPost} from "../model/UsuarioKeycloak";

interface KeycloakContextProps {
    initialized: boolean;
    authenticated: boolean;
    keycloak: KeycloakInstance;
    user: KeycloakProfile | null;
    token: string | null;
    logout: () => void;
    hasRole: (roles: string | string[]) => boolean;
    hasRealmRole: (role: string) => boolean;
    hasResourceRole: (role: string, resource?: string) => boolean;
    roles: {
        realm: string[];
        resource: Record<string, string[]>;
    };
}

interface KeycloakProviderProps {
    children: ReactNode;
}

export const KeycloakContext = createContext<KeycloakContextProps | null>(null);

export const KeycloakProvider: React.FC<KeycloakProviderProps> = ({ children }) => {
    const [authenticated, setAuthenticated] = useState<boolean>(false);
    const [initialized, setInitialized] = useState<boolean>(false);
    const [user, setUser] = useState<KeycloakProfile | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [roles, setRoles] = useState<{ realm: string[], resource: Record<string, string[]> }>({
        realm: [],
        resource: {}
    });

    useEffect(() => {
        const initKeycloak = async () => {
            try {
                keycloak.onAuthSuccess = () => {
                    console.log('✅ Login realizado com sucesso!');
                };

                const auth = await keycloak.init({
                    onLoad: 'login-required',
                    pkceMethod: 'S256',
                    checkLoginIframe: false,
                    enableLogging: false,
                    redirectUri: window.location.origin
                });

                setAuthenticated(auth);
                setInitialized(true);

                if (auth) {
                    setToken(keycloak.token);
                    // Adicionar perfil de usuário se necessário
                    try {
                        const userProfile = await keycloak.loadUserProfile();
                        setUser(userProfile);

                        const rolesClienteDoUsuario = keycloak.realmAccess?.roles
                            .filter((f: any) => f === TipoUsuarioKeycloak.CLIENTE);
                        const rolesAdminDoUsuario = keycloak.realmAccess?.roles
                            .filter((f: any) => f === TipoUsuarioKeycloak.ADMIN);
                        const rolesConsumidorDoUsuario = keycloak.realmAccess?.roles
                            .filter((f: any) => f === TipoUsuarioKeycloak.CONSUMIDOR);

                        if (rolesConsumidorDoUsuario.length !== 0 && rolesClienteDoUsuario.length === 0) {
                            keycloak.logout();
                            return;
                        }

                        if (rolesClienteDoUsuario?.length === 0 && rolesAdminDoUsuario?.length === 0) {
                            const entidade: UsuarioKeycloakPost = {
                                id: userProfile.id,
                            };

                            await Ajax.of()
                                .setTokenKeycloak(keycloak.token!)
                                .postRegistroCustom(`${rest.server}${rest.usuario}/${rest.atribuirRoleCliente}`, entidade);

                            window.location.href = "/";
                        }
                    } catch (profileError) {
                        console.error('Failed to load user profile:', profileError);
                    }

                    // Extrair roles
                    const realmRoles = keycloak.realmAccess?.roles || [];
                    const resourceRoles: Record<string, string[]> = {};

                    // Processar resource roles (client roles)
                    const resourceAccess = keycloak.resourceAccess || {};
                    Object.keys(resourceAccess).forEach(resource => {
                        resourceRoles[resource] = resourceAccess[resource]?.roles || [];
                    });

                    setRoles({
                        realm: realmRoles,
                        resource: resourceRoles
                    });

                    // Configurar refresh do token
                    refreshToken();
                }
            } catch (error) {
                console.error('Keycloak init error:', error);
                setInitialized(true);
            }
        };

        initKeycloak();
    }, []);

    // Função para atualizar o token periodicamente
    const refreshToken = () => {
        const minValidity = 70; // segundos
        keycloak.updateToken(minValidity)
            .then((refreshed: any) => {
                if (refreshed) {
                    setToken(keycloak.token);
                }
                // Agendar próxima atualização
                setTimeout(refreshToken, 60000); // a cada 1 minuto
            })
            .catch((error: any) => {
                console.error('Failed to refresh token:', error);
                keycloak.login(); // Força novo login se o refresh falhar
            });
    };

    const logout = () => {
        keycloak.logout();
    };

    // Função para verificar se o usuário tem uma ou mais roles
    const hasRole = (roles: string | string[]): boolean => {
        if (!authenticated) return false;

        const rolesToCheck = Array.isArray(roles) ? roles : [roles];

        // Verificar realm roles
        for (const role of rolesToCheck) {
            if (keycloak.hasRealmRole(role)) return true;
        }

        // Verificar resource roles em todos os clients
        const resourceAccess = keycloak.resourceAccess || {};
        for (const client in resourceAccess) {
            for (const role of rolesToCheck) {
                if (keycloak.hasResourceRole(role, client)) return true;
            }
        }

        return false;
    };

    // Função específica para verificar realm roles
    const hasRealmRole = (role: string): boolean => {
        if (!authenticated) return false;
        return keycloak.hasRealmRole(role);
    };

    // Função específica para verificar resource roles
    const hasResourceRole = (role: string, resource?: string): boolean => {
        if (!authenticated) return false;
        return keycloak.hasResourceRole(role, resource);
    };

    const keycloakValue: KeycloakContextProps = {
        initialized,
        authenticated,
        keycloak,
        user,
        token,
        logout,
        hasRole,
        hasRealmRole,
        hasResourceRole,
        roles
    };

    return (
        <KeycloakContext.Provider value={keycloakValue}>
            {children}
        </KeycloakContext.Provider>
    );
};